import { useMutation, useQueryClient } from '@tanstack/react-query';
import { backendClient } from '@/lib/backendClient';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useSimpleRateLimit } from '@/hooks/useSimpleRateLimit';
import { handleSecureError, secureLog } from '@/utils/secureErrorHandler';
import { sanitizeInput, RATE_LIMITS } from '@/utils/inputValidation';
import { UserPreference, SessionFeedback } from '@/types/userPreferences';
import { userPreferencesKeys } from '@/hooks/queries/userPreferencesKeys';

export const useUserPreferencesMutations = (preferences: UserPreference[]) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { checkRateLimit } = useSimpleRateLimit();
  const queryClient = useQueryClient();

  // Update preference mutation
  const updatePreferenceMutation = useMutation({
    mutationFn: async ({
      category,
      preferenceType,
      value,
      confidenceBoost = 0.1
    }: {
      category: string;
      preferenceType: string;
      value: string;
      confidenceBoost?: number;
    }) => {
      if (!user) throw new Error('User not authenticated');

      // Validate and sanitize inputs
      const sanitizedCategory = sanitizeInput(category).substring(0, 50);
      const sanitizedType = sanitizeInput(preferenceType).substring(0, 50);
      const sanitizedValue = sanitizeInput(value).substring(0, 500);
      const validConfidenceBoost = Math.max(0, Math.min(1, confidenceBoost));

      // Check if preference exists
      const existing = preferences.find(
        p => p.category === sanitizedCategory && p.preference_type === sanitizedType
      );

      if (existing) {
        // Update existing preference
        const newConfidence = Math.min(1, existing.confidence_score + validConfidenceBoost);
        
        const { error } = await backendClient
          .from('user_preferences')
          .update({
            preference_value: sanitizedValue,
            confidence_score: newConfidence,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);

        if (error) throw error;
        return { ...existing, preference_value: sanitizedValue, confidence_score: newConfidence };
      } else {
        // Create new preference
        const { data, error } = await backendClient
          .from('user_preferences')
          .insert([{
            user_id: user.id,
            category: sanitizedCategory,
            preference_type: sanitizedType,
            preference_value: sanitizedValue,
            confidence_score: 0.3
          }])
          .select()
          .single();

        if (error) throw error;
        return data as UserPreference;
      }
    },
    onSuccess: (updatedPreference) => {
      // Optimistically update the cache
      queryClient.setQueryData(
        userPreferencesKeys.list(user?.id || ''),
        (oldData: UserPreference[] | undefined) => {
          if (!oldData) return [updatedPreference];
          
          const existingIndex = oldData.findIndex(p => p.id === updatedPreference.id);
          if (existingIndex >= 0) {
            const newData = [...oldData];
            newData[existingIndex] = updatedPreference;
            return newData.sort((a, b) => b.confidence_score - a.confidence_score);
          } else {
            return [updatedPreference, ...oldData].sort((a, b) => b.confidence_score - a.confidence_score);
          }
        }
      );

      secureLog('info', 'User preference updated successfully', { 
        category: updatedPreference.category, 
        type: updatedPreference.preference_type 
      });
    },
    onError: (error) => {
      const secureError = handleSecureError(error, 'user_preferences_update');
      secureLog('error', 'Failed to update user preference', { error: secureError.message });
    }
  });

  // Submit session feedback mutation
  const submitFeedbackMutation = useMutation({
    mutationFn: async (feedback: SessionFeedback) => {
      if (!user) throw new Error('User not authenticated');

      // Check rate limit
      const canProceed = await checkRateLimit(RATE_LIMITS.CONTENT_GENERATION);
      if (!canProceed) throw new Error('Rate limit exceeded');

      // Validate and sanitize feedback
      const sanitizedFeedback = {
        ...feedback,
        session_id: sanitizeInput(feedback.session_id).substring(0, 255),
        rating: Math.max(1, Math.min(5, Math.floor(feedback.rating))),
        completion_percentage: Math.max(0, Math.min(100, Math.floor(feedback.completion_percentage))),
        liked_aspects: feedback.liked_aspects.map(aspect => sanitizeInput(aspect).substring(0, 200)).slice(0, 10),
        disliked_aspects: feedback.disliked_aspects.map(aspect => sanitizeInput(aspect).substring(0, 200)).slice(0, 10),
        preferred_soundscape: feedback.preferred_soundscape ? sanitizeInput(feedback.preferred_soundscape).substring(0, 100) : undefined,
        energy_level_before: Math.max(1, Math.min(10, Math.floor(feedback.energy_level_before))),
        energy_level_after: Math.max(1, Math.min(10, Math.floor(feedback.energy_level_after)))
      };

      // Note: Session feedback storage disabled - table not available
      // The feedback is still analyzed for preference updates
      return sanitizedFeedback;
    },
    onSuccess: async (feedback) => {
      // Analyze feedback and update preferences
      await analyzeAndUpdatePreferences(feedback, updatePreferenceMutation.mutate);
      
      toast({
        title: "Thank you for your feedback!",
        description: "We'll use this to improve your future sessions.",
      });

      secureLog('info', 'Session feedback submitted successfully', { 
        sessionId: feedback.session_id 
      });
    },
    onError: (error) => {
      const secureError = handleSecureError(error, 'session_feedback_submit');
      toast({
        title: "Error",
        description: secureError.userMessage,
        variant: "destructive",
      });
    }
  });

  const analyzeAndUpdatePreferences = async (
    feedback: SessionFeedback,
    updatePreference: typeof updatePreferenceMutation.mutate
  ) => {
    // Analyze completion percentage
    if (feedback.completion_percentage > 80) {
      if (feedback.preferred_soundscape) {
        updatePreference({
          category: 'soundscape',
          preferenceType: 'soundscape',
          value: feedback.preferred_soundscape,
          confidenceBoost: 0.2
        });
      }
    }

    // Analyze rating
    if (feedback.rating >= 4) {
      feedback.liked_aspects.forEach((aspect) => {
        updatePreference({
          category: 'content_style',
          preferenceType: 'content_style',
          value: aspect,
          confidenceBoost: 0.15
        });
      });
    }

    // Energy level improvement
    const energyImprovement = feedback.energy_level_after - feedback.energy_level_before;
    if (energyImprovement > 2) {
      updatePreference({
        category: 'effectiveness',
        preferenceType: 'content_style',
        value: 'high',
        confidenceBoost: 0.1
      });
    }
  };

  return {
    updatePreference: updatePreferenceMutation.mutate,
    updatingPreference: updatePreferenceMutation.isPending,
    submitSessionFeedback: submitFeedbackMutation.mutate,
    submittingFeedback: submitFeedbackMutation.isPending,
  };
};
